import { BankOutlined, EditOutlined, RedoOutlined, StopOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useState } from 'react';

import { ApiError, type OffsetPageInfo, api } from '../api/client';
import type { components } from '../api/schema.gen';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { Modal } from '../components/Modal';
import { OnboardingNotice } from '../components/OnboardingNotice';
import { OffsetPager } from '../components/Pagination';
import { PageHeading } from '../components/PageHeading';
import { PageLoader } from '../components/PageLoader';
import { SelectMenu } from '../components/SelectMenu';
import { formatDate, formatDateTime } from '../lib/dateFormat';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { formatTHB } from '../lib/money';
import { fadeUp } from '../lib/motionPresets';
import { useSession } from '../lib/SessionContext';

type Member = components['schemas']['Member'];
type PendingInvite = components['schemas']['PendingInvite'];
type InviteHistoryEntry = components['schemas']['InviteHistoryEntry'];
type TeamLimitGroup = components['schemas']['TeamLimitGroup'];
type MemberBudget = components['schemas']['MemberBudget'];
type BudgetPeriod = 'daily' | 'monthly';

// LLMR-per-member-spend-limit Phase 4 (write path only — nothing yet
// enforces these limits; Phase 2/3 are unbuilt). `kind` distinguishes the
// org-default row (member_budgets.user_id NULL) from a member's own
// override, rather than inferring it from whether `userId` happens to be
// set — the "add an override" flow starts with no member chosen yet, which
// would otherwise be indistinguishable from "editing the org default".
interface BudgetModalState {
  kind: 'default' | 'member';
  userId?: string;
  /** True once this modal targets an EXISTING row (org default, or a
   * specific member's override) — locks the member picker and period
   * select, since changing either would silently target a different row
   * instead of updating this one. False only for "add an override", where
   * both are still being chosen. */
  locked: boolean;
  periodType: BudgetPeriod;
  amount: string;
}

interface BudgetRemoveTarget {
  kind: 'default' | 'member';
  userId?: string;
  periodType: BudgetPeriod;
}

// LLMR-139: page-size picker matches Reports / Usage (10 / 20 / 50). History
// is the one table on this screen that can genuinely grow without bound,
// since nothing ever ages back out of it — roster/pending still fetch one
// generous page and paginate nowhere.
const HISTORY_PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const DEFAULT_HISTORY_PAGE_SIZE = 10;

// A team is built by hand, one invite at a time (server-side comment on
// store.ListMembers) — one page comfortably holds it, and this is generous
// enough that the caller's own row is always on it. See client.ts's `team`
// for why that matters: this screen works out its own role from the roster
// rather than duplicating the server's primary-organization resolution.
const PAGE_SIZE = 100;

export function Team() {
  const { ready, hasOrg, me, isB2BOrg } = useSession();
  const { dict } = useLanguage();
  const [onboarding, setOnboarding] = useState(false);
  // Same lookup OrgSwitcher uses (LLMR-54) — this page is entirely scoped to
  // one org, and without this label the only place that org's name appears
  // anywhere on screen is the topbar switcher, easy to miss entirely.
  const activeOrg = me?.organizations?.find((o) => o.org_id === me.active_org_id) ?? me?.organizations?.[0];

  const [members, setMembers] = useState<Member[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  // From GET /team's own `limits` — the exact same cap teamLimitReached
  // enforces server-side, shown up front instead of only discoverable by
  // hitting the 409 (LLMR-team-member-limit).
  const [limits, setLimits] = useState<{ owner_admin: TeamLimitGroup; member: TeamLimitGroup } | null>(null);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  // A string, not a number, deliberately — matches `role` above (also a
  // discrete-option select backed by a string union) rather than mixing a
  // numeric-typed value into a <select>'s controlled value, which is a
  // recipe for a select that renders correctly but silently keeps whatever
  // it was on submit.
  const [expiresInDays, setExpiresInDays] = useState<'1' | '3' | '7' | '30' | '365'>('1');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  // Email only — render with dict.team.inviteSent at paint time so switching
  // EN/TH does not leave a stale Thai/English sentence on screen.
  const [inviteSuccessEmail, setInviteSuccessEmail] = useState<string | null>(null);

  const [roleBusyId, setRoleBusyId] = useState<string | null>(null);
  const [roleChangeError, setRoleChangeError] = useState<string | null>(null);

  const [removeTarget, setRemoveTarget] = useState<Member | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const [invites, setInvites] = useState<PendingInvite[] | null>(null);
  const [invitesError, setInvitesError] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PendingInvite | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const [history, setHistory] = useState<InviteHistoryEntry[] | null>(null);
  const [historyPageInfo, setHistoryPageInfo] = useState<OffsetPageInfo | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(DEFAULT_HISTORY_PAGE_SIZE);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [budgets, setBudgets] = useState<MemberBudget[] | null>(null);
  const [budgetsError, setBudgetsError] = useState<string | null>(null);
  const [budgetModal, setBudgetModal] = useState<BudgetModalState | null>(null);
  const [budgetSaveBusy, setBudgetSaveBusy] = useState(false);
  const [budgetSaveError, setBudgetSaveError] = useState<string | null>(null);
  const [budgetRemoveTarget, setBudgetRemoveTarget] = useState<BudgetRemoveTarget | null>(null);
  const [budgetRemoveBusy, setBudgetRemoveBusy] = useState(false);
  const [budgetRemoveError, setBudgetRemoveError] = useState<string | null>(null);

  const [editTarget, setEditTarget] = useState<PendingInvite | null>(null);
  const [editRole, setEditRole] = useState<'member' | 'admin'>('member');
  // '' means "don't change" — deliberately distinct from any real choice, so
  // opening Edit and only touching the role (leaving this alone) cannot
  // silently reset a 30-day invite back down to whatever this defaulted to.
  const [editExpiresInDays, setEditExpiresInDays] = useState<'' | '1' | '3' | '7' | '30' | '365'>('');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.team({ page_size: PAGE_SIZE });
      setMembers(res.data);
      setLimits(res.limits);
      setListError(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) setOnboarding(true);
      else setListError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.team.loadError);
    }
  }, [dict]);

  useEffect(() => {
    if (!ready) return;
    if (!hasOrg) {
      setOnboarding(true);
      return;
    }
    void load();
  }, [ready, hasOrg, load]);

  // My own role in THIS organization — read back from the roster (already
  // fetched for this page) rather than `me.organizations`. As of LLMR-54
  // the latter also carries every org the account belongs to, with
  // `me.active_org_id` marking which one is current — either source now
  // agrees; this one is here because the roster call was already made.
  const myRole = members?.find((m) => m.user_id === me?.user_id)?.role;
  const canManage = myRole === 'owner' || myRole === 'admin';
  const isOwner = myRole === 'owner';

  // Owner/admin only — GET /team/invites answers 403 for a plain member, so
  // this only ever fires once the roster above has resolved canManage true.
  const loadInvites = useCallback(async () => {
    try {
      const res = await api.pendingInvites({ page_size: PAGE_SIZE });
      setInvites(res.data);
      setInvitesError(null);
    } catch (e) {
      setInvitesError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.team.invitesLoadError);
    }
  }, [dict]);

  useEffect(() => {
    if (!canManage) return;
    void loadInvites();
  }, [canManage, loadInvites]);

  // LLMR-139. Same owner/admin gate as loadInvites — a fresh section, so it
  // gets its own busy flag rather than reusing invites-panel state.
  const loadHistory = useCallback(async (page: number, size = historyPageSize) => {
    setHistoryBusy(true);
    try {
      const res = await api.inviteHistory({ page, page_size: size });
      setHistory(res.data);
      setHistoryPageInfo(res.page);
      setHistoryError(null);
    } catch (e) {
      setHistoryError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.team.historyLoadError);
    } finally {
      setHistoryBusy(false);
    }
  }, [dict, historyPageSize]);

  useEffect(() => {
    if (!canManage) return;
    void loadHistory(historyPage);
  }, [canManage, historyPage, loadHistory]);

  // LLMR-per-member-spend-limit Phase 4. Gated the same way as the section
  // itself renders below (owner/admin AND a b2b org) — GET /team/budgets
  // answers 403 for either an individual-type org or a plain member, so
  // there is no point issuing it otherwise.
  const loadBudgets = useCallback(async () => {
    try {
      const res = await api.memberBudgets();
      setBudgets(res.data);
      setBudgetsError(null);
    } catch (e) {
      setBudgetsError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.team.budgets.loadError);
    }
  }, [dict]);

  useEffect(() => {
    if (!canManage || !isB2BOrg) return;
    void loadBudgets();
  }, [canManage, isB2BOrg, loadBudgets]);

  function memberName(userId?: string): string {
    const m = members?.find((mm) => mm.user_id === userId);
    return m ? m.display_name || m.email : '';
  }

  function openDefaultBudgetModal(periodType: BudgetPeriod) {
    const row = budgets?.find((b) => !b.user_id && b.period_type === periodType);
    setBudgetModal({ kind: 'default', locked: true, periodType, amount: row?.limit.amount ?? '' });
    setBudgetSaveError(null);
  }

  function openOverrideEditModal(row: MemberBudget) {
    setBudgetModal({ kind: 'member', userId: row.user_id, locked: true, periodType: row.period_type, amount: row.limit.amount });
    setBudgetSaveError(null);
  }

  function openAddOverrideModal() {
    setBudgetModal({ kind: 'member', locked: false, periodType: 'daily', amount: '' });
    setBudgetSaveError(null);
  }

  async function submitBudgetModal(e: React.FormEvent) {
    e.preventDefault();
    if (!budgetModal) return;
    if (budgetModal.kind === 'member' && !budgetModal.userId) return; // guarded by disabled submit too
    setBudgetSaveBusy(true);
    setBudgetSaveError(null);
    try {
      if (budgetModal.kind === 'default') {
        await api.setOrgDefaultBudget(budgetModal.periodType, budgetModal.amount);
      } else {
        await api.setMemberBudget(budgetModal.userId as string, budgetModal.periodType, budgetModal.amount);
      }
      setBudgetModal(null);
      await loadBudgets();
    } catch (e) {
      setBudgetSaveError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.team.budgets.saveError);
    } finally {
      setBudgetSaveBusy(false);
    }
  }

  async function confirmRemoveBudget() {
    if (!budgetRemoveTarget) return;
    setBudgetRemoveBusy(true);
    setBudgetRemoveError(null);
    try {
      if (budgetRemoveTarget.kind === 'default') {
        await api.deleteOrgDefaultBudget(budgetRemoveTarget.periodType);
      } else {
        await api.deleteMemberBudget(budgetRemoveTarget.userId as string, budgetRemoveTarget.periodType);
      }
      setBudgetRemoveTarget(null);
      await loadBudgets();
    } catch (e) {
      setBudgetRemoveError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.team.budgets.removeError);
    } finally {
      setBudgetRemoveBusy(false);
    }
  }

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    setInviteError(null);
    setInviteSuccessEmail(null);

    // Cheap, immediate check against the pending-invites list already
    // loaded for the table below — catches the common case (the email is
    // right there on screen) without a round trip. Not the source of
    // truth: the server re-checks under a unique index (see the 409 branch
    // below), which is what actually closes the race where a pending
    // invite appears after this list was fetched.
    const trimmed = email.trim();
    if (invites?.some((inv) => inv.email.toLowerCase() === trimmed.toLowerCase())) {
      setInviteError(dict.team.inviteErrorAlreadyPending);
      return;
    }

    setInviting(true);
    try {
      const res = await api.addMember(trimmed, role, Number(expiresInDays));
      setInviteSuccessEmail(res.email);
      await loadInvites();
      setEmail('');
      setRole('member');
      setExpiresInDays('1');
    } catch (e) {
      if (e instanceof ApiError && e.is('/problems/invite-already-pending')) {
        setInviteError(dict.team.inviteErrorAlreadyPending);
        await loadInvites();
      } else if (e instanceof ApiError && e.is('/problems/team-limit-reached')) {
        // Distinct from the "already a member" 409 below — the server's
        // own Detail already names the actual cap, so there is no
        // hand-maintained translated string to keep in sync with it.
        setInviteError(e.problem.detail ?? e.problem.title);
      } else if (e instanceof ApiError && e.status === 409) {
        setInviteError(dict.team.inviteErrorAlreadyMember(isB2BOrg));
      } else {
        setInviteError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.team.inviteError);
      }
    } finally {
      setInviting(false);
    }
  }

  // A resend explicitly targets a row already in the table below — passing
  // resend:true is what tells the server this is not a duplicate submission
  // to refuse (see invite() above), but the one deliberate case that should
  // still refresh the existing pending row: new token/expiry, old link
  // stops working.
  async function resend(inv: PendingInvite) {
    setResendingId(inv.invite_id);
    setResendError(null);
    try {
      await api.resendInvite(inv.email, inv.role);
      await loadInvites();
    } catch (e) {
      setResendError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.team.resendError);
    } finally {
      setResendingId(null);
    }
  }

  async function confirmRevoke() {
    if (!revokeTarget) return;
    setRevokeBusy(true);
    setRevokeError(null);
    try {
      await api.revokeInvite(revokeTarget.invite_id);
      setRevokeTarget(null);
      await loadInvites();
    } catch (e) {
      setRevokeError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.team.cancelInviteError);
    } finally {
      setRevokeBusy(false);
    }
  }

  function openEditInvite(inv: PendingInvite) {
    setEditTarget(inv);
    setEditRole(inv.role);
    setEditExpiresInDays('');
    setEditError(null);
  }

  async function confirmEditInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!editTarget) return;
    setEditBusy(true);
    setEditError(null);
    try {
      await api.updateInvite(editTarget.invite_id, {
        // Role is only ever shown/editable to the owner (see the form
        // below) — an admin never sends it, so their edit can never touch
        // role even implicitly.
        ...(isOwner ? { role: editRole } : {}),
        ...(editExpiresInDays ? { expiresInDays: Number(editExpiresInDays) } : {}),
      });
      setEditTarget(null);
      await loadInvites();
    } catch (e) {
      setEditError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.team.editInviteError);
    } finally {
      setEditBusy(false);
    }
  }

  async function changeRole(m: Member, next: 'member' | 'admin') {
    if (m.role === next) return;
    setRoleBusyId(m.user_id);
    setRoleChangeError(null);
    try {
      await api.updateMemberRole(m.user_id, next);
      await load();
    } catch (e) {
      // Every other failure: the row simply does not update; a reload of
      // the whole list on a failed single-row edit would be more disruptive
      // than the select silently reverting to what the server actually
      // holds on next load. Hitting the team-size cap is the one case
      // worth an explicit message — it isn't a transient failure, and the
      // fix (remove someone, or contact sales) is something to actually
      // tell the customer, not something a reload alone explains.
      if (e instanceof ApiError && e.is('/problems/team-limit-reached')) {
        setRoleChangeError(e.problem.detail ?? e.problem.title);
      }
    } finally {
      setRoleBusyId(null);
    }
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    setRemoveBusy(true);
    setRemoveError(null);
    try {
      await api.removeMember(removeTarget.user_id);
      setRemoveTarget(null);
      await load();
    } catch (e) {
      setRemoveError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.team.removeError);
    } finally {
      setRemoveBusy(false);
    }
  }

  return (
    <div className="page team-page">
      {activeOrg && (
        <p className="active-org-badge">
          <BankOutlined aria-hidden="true" />
          {activeOrg.name}
        </p>
      )}
      <PageHeading subtitle={dict.team.subtitle(isB2BOrg)}>{dict.common.team}</PageHeading>

      {onboarding ? (
        <OnboardingNotice area={dict.onboarding.teamArea} />
      ) : (
        <>
          {canManage && (
            <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel team-panel team-panel--invite">
              <h2>{dict.team.inviteHeading}</h2>
              {limits && (
                <p className="note team-limits-note">
                  {limits.owner_admin.max != null && (
                    <span
                      className="team-limits-admins"
                      data-tooltip={dict.team.limitsOwnerAdminHint}
                      tabIndex={0}
                    >
                      {dict.team.limitsOwnerAdmin(limits.owner_admin.used, limits.owner_admin.max)}
                    </span>
                  )}
                  {limits.owner_admin.max != null && <span aria-hidden="true"> · </span>}
                  {limits.member.max != null
                    ? dict.team.limitsMember(limits.member.used, limits.member.max)
                    : dict.team.limitsMemberUnlimited(limits.member.used)}
                </p>
              )}
              <form onSubmit={invite} className="team-invite-form">
                <div className="team-invite-fields">
                  <div className="inline-form-field team-invite-email">
                    <label htmlFor="team-email">{dict.team.emailLabel}</label>
                    <input
                      id="team-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={dict.team.emailPlaceholder}
                      required
                    />
                  </div>
                  <div className="inline-form-field">
                    <label htmlFor="team-role">{dict.team.roleLabel}</label>
                    <SelectMenu
                      id="team-role"
                      aria-label={dict.team.roleLabel}
                      value={role}
                      onChange={setRole}
                      options={[
                        { value: 'member', label: dict.team.role.member },
                        { value: 'admin', label: dict.team.role.admin },
                      ]}
                    />
                  </div>
                  <div className="inline-form-field">
                    {/* Server default/ceiling: 1–365 days. */}
                    <label htmlFor="team-expires">{dict.team.expiresInLabel}</label>
                    <SelectMenu
                      id="team-expires"
                      aria-label={dict.team.expiresInLabel}
                      value={expiresInDays}
                      onChange={setExpiresInDays}
                      options={[
                        { value: '1', label: dict.team.expiresInOptions['1'] },
                        { value: '3', label: dict.team.expiresInOptions['3'] },
                        { value: '7', label: dict.team.expiresInOptions['7'] },
                        { value: '30', label: dict.team.expiresInOptions['30'] },
                        { value: '365', label: dict.team.expiresInOptions['365'] },
                      ]}
                    />
                  </div>
                  <button type="submit" disabled={inviting || email.trim() === ''}>
                    {inviting ? dict.team.inviting : dict.team.inviteButton}
                  </button>
                </div>
                {inviteError && (
                  <p className="field-error team-invite-error" role="alert">
                    {inviteError}
                  </p>
                )}
                {inviteSuccessEmail && (
                  <p className="note">{dict.team.inviteSent(inviteSuccessEmail)}</p>
                )}
              </form>
            </motion.section>
          )}

          <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel team-panel team-panel--roster">
            <h2>{dict.team.rosterHeading}</h2>
            {listError && <div className="err">{listError}</div>}
            {roleChangeError && <div className="err">{roleChangeError}</div>}

            {members === null ? (
              <PageLoader />
            ) : (
            <div className="table-scroll">
              <table role="table" className="team-table">
                <thead role="rowgroup">
                  <tr role="row">
                    <th role="columnheader">{dict.team.colName}</th>
                    <th role="columnheader">{dict.team.colEmail}</th>
                    <th role="columnheader">{dict.team.colRole}</th>
                    <th role="columnheader">{dict.team.colJoined}</th>
                    <th role="columnheader" aria-label={dict.team.actionsAria} />
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {members.length === 0 && (
                    <tr role="row">
                      <td role="cell" colSpan={5}>
                        <EmptyState title={dict.team.emptyTitle}>
                          <p className="note">{dict.team.emptyBody(isB2BOrg)}</p>
                        </EmptyState>
                      </td>
                    </tr>
                  )}
                  {members.map((m) => {
                    const self = m.user_id === me?.user_id;
                    // Owner: anyone but the owner row (ownership transfer is
                    // a separate flow — matching the 409 the server returns).
                    // Admin: plain members only (promote to admin / remove).
                    // Existing admin rows stay read-only — demotion and peer
                    // removal are owner-only.
                    const canEditRole =
                      m.role !== 'owner' && (isOwner || (myRole === 'admin' && m.role === 'member'));
                    const canRemove =
                      canManage && m.role !== 'owner' && !self && (isOwner || m.role === 'member');
                    return (
                      <tr role="row" key={m.user_id}>
                        <td role="cell" data-label={dict.team.colName}>
                          <span className="team-member-name">
                            {m.display_name || m.email}
                            {self && <span className="muted"> ({dict.team.you})</span>}
                          </span>
                        </td>
                        <td role="cell" data-label={dict.team.colEmail}>
                          {m.email}
                        </td>
                        <td role="cell" data-label={dict.team.colRole}>
                          {canEditRole ? (
                            <SelectMenu
                              aria-label={dict.team.roleLabel}
                              value={m.role === 'admin' ? 'admin' : 'member'}
                              disabled={roleBusyId === m.user_id}
                              onChange={(next) => void changeRole(m, next)}
                              options={[
                                { value: 'member', label: dict.team.role.member },
                                { value: 'admin', label: dict.team.role.admin },
                              ]}
                            />
                          ) : (
                            <span className={`badge badge--role-${m.role}`}>{dict.team.role[m.role]}</span>
                          )}
                        </td>
                        <td role="cell" data-label={dict.team.colJoined}>
                          {formatDate(m.created_at)}
                        </td>
                        <td role="cell" className="num" data-label={dict.team.actionsAria}>
                          {canRemove && (
                            <button type="button" className="ghost danger-text" onClick={() => setRemoveTarget(m)}>
                              {dict.team.remove}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}
          </motion.section>

          {canManage && (
            <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel team-panel team-panel--invites">
              <h2>{dict.team.pendingInvitesHeading}</h2>
              {invitesError && <div className="err">{invitesError}</div>}
              {resendError && <div className="err">{resendError}</div>}

              {invites === null ? (
                <PageLoader />
              ) : (
              <div className="table-scroll">
                <table role="table" className="team-table">
                  <thead role="rowgroup">
                    <tr role="row">
                      <th role="columnheader">{dict.team.colEmail}</th>
                      <th role="columnheader">{dict.team.colRole}</th>
                      <th role="columnheader">{dict.team.colExpires}</th>
                      <th role="columnheader" aria-label={dict.team.actionsAria} />
                    </tr>
                  </thead>
                  <tbody role="rowgroup">
                    {invites.length === 0 && (
                      <tr role="row">
                        <td role="cell" colSpan={4}>
                          <p className="note">{dict.team.pendingInvitesEmpty}</p>
                        </td>
                      </tr>
                    )}
                    {invites.map((inv) => {
                      // Edit and cancel keep the admin-cannot-touch-admin
                      // asymmetry — only the owner may touch an admin-role
                      // invite, matching what the server itself refuses.
                      // Resend has no such limit: an admin may resend any
                      // invite it could have created, admin-role included.
                      const canEditOrRevokeThis = isOwner || inv.role !== 'admin';
                      return (
                        <tr role="row" key={inv.invite_id}>
                          <td role="cell" data-label={dict.team.colEmail}>
                            <span className="team-member-name">{inv.email}</span>
                          </td>
                          <td role="cell" data-label={dict.team.colRole}>
                            <span className={`badge badge--role-${inv.role}`}>{dict.team.role[inv.role]}</span>
                          </td>
                          <td role="cell" data-label={dict.team.colExpires}>
                            {formatDateTime(inv.expires_at)}
                          </td>
                          <td role="cell" className="num" data-label={dict.team.actionsAria}>
                            <div className="row-actions">
                              {canEditOrRevokeThis && (
                                <button
                                  type="button"
                                  className="ghost icon-btn"
                                  data-tooltip={dict.team.editInvite}
                                  aria-label={dict.team.editInvite}
                                  onClick={() => openEditInvite(inv)}
                                >
                                  <EditOutlined aria-hidden="true" />
                                </button>
                              )}
                              <button
                                type="button"
                                className="ghost icon-btn"
                                data-tooltip={resendingId === inv.invite_id ? dict.team.resending : dict.team.resend}
                                aria-label={resendingId === inv.invite_id ? dict.team.resending : dict.team.resend}
                                disabled={resendingId === inv.invite_id}
                                onClick={() => void resend(inv)}
                              >
                                <RedoOutlined aria-hidden="true" />
                              </button>
                              {canEditOrRevokeThis && (
                                <button
                                  type="button"
                                  className="ghost danger-text icon-btn"
                                  data-tooltip={dict.team.cancelInvite}
                                  aria-label={dict.team.cancelInvite}
                                  onClick={() => setRevokeTarget(inv)}
                                >
                                  <StopOutlined aria-hidden="true" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </motion.section>
          )}

          {canManage && (
            <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel team-panel team-panel--invite-history">
              <h2>{dict.team.historyHeading}</h2>
              <p className="panel-sub">{dict.team.historySubtitle}</p>
              {historyError && <div className="err">{historyError}</div>}

              {history === null ? (
                <PageLoader />
              ) : (
              <div className="table-scroll">
                <table role="table" className="team-table">
                  <thead role="rowgroup">
                    <tr role="row">
                      <th role="columnheader">{dict.team.colEmail}</th>
                      <th role="columnheader">{dict.team.colRole}</th>
                      <th role="columnheader">{dict.team.colInvitedBy}</th>
                      <th role="columnheader">{dict.team.colStatus}</th>
                      <th role="columnheader">{dict.team.colOccurredAt}</th>
                    </tr>
                  </thead>
                  <tbody role="rowgroup">
                    {history.length === 0 && (
                      <tr role="row">
                        <td role="cell" colSpan={5}>
                          <p className="note">{dict.team.historyEmpty}</p>
                        </td>
                      </tr>
                    )}
                    {history.map((entry, i) => (
                      <tr role="row" key={`${entry.email}-${entry.occurred_at}-${i}`}>
                        <td role="cell" data-label={dict.team.colEmail}>
                          <span className="team-member-name">{entry.email}</span>
                        </td>
                        <td role="cell" data-label={dict.team.colRole}>
                          <span className={`badge badge--role-${entry.role}`}>{dict.team.role[entry.role]}</span>
                        </td>
                        <td role="cell" data-label={dict.team.colInvitedBy}>
                          {entry.invited_by.display_name ?? entry.invited_by.email ?? dict.team.historyInvitedByUnknown}
                        </td>
                        <td role="cell" data-label={dict.team.colStatus}>
                          <span className={`badge badge--status-${entry.status}`}>{dict.team.historyStatus[entry.status]}</span>
                        </td>
                        <td role="cell" data-label={dict.team.colOccurredAt}>
                          {formatDate(entry.occurred_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
              <OffsetPager
                page={historyPageInfo}
                busy={historyBusy}
                onPage={setHistoryPage}
                pageSize={historyPageSize}
                pageSizeOptions={[...HISTORY_PAGE_SIZE_OPTIONS]}
                onPageSizeChange={(size) => {
                  setHistoryPageSize(size);
                  setHistoryPage(1);
                }}
              />
            </motion.section>
          )}

          {isB2BOrg && canManage && (
            <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel team-panel team-panel--budgets">
              <h2>{dict.team.budgets.heading}</h2>
              <p className="panel-sub">{dict.team.budgets.subtitle}</p>
              <p className="note note-warning team-budgets-warning">{dict.team.budgets.warning}</p>
              {budgetsError && <div className="err">{budgetsError}</div>}

              {budgets === null ? (
                <PageLoader />
              ) : (
                <>
                  <div className="team-budget-defaults">
                    {(['daily', 'monthly'] as const).map((period) => {
                      const row = budgets.find((b) => !b.user_id && b.period_type === period);
                      return (
                        <div className="team-budget-default-card" key={period}>
                          <span className="team-budget-default-label">{dict.team.budgets.periodLabel[period]}</span>
                          <span className="team-budget-default-value">
                            {row ? formatTHB(row.limit.amount) : dict.team.budgets.notSet}
                          </span>
                          <div className="row-actions">
                            <button type="button" className="ghost" onClick={() => openDefaultBudgetModal(period)}>
                              {row ? dict.team.budgets.editButton : dict.team.budgets.setButton}
                            </button>
                            {row && (
                              <button
                                type="button"
                                className="ghost danger-text"
                                onClick={() => setBudgetRemoveTarget({ kind: 'default', periodType: period })}
                              >
                                {dict.team.budgets.removeButton}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="team-budget-overrides">
                    <div className="team-budget-overrides-header">
                      <h3>{dict.team.budgets.overridesHeading}</h3>
                      <button type="button" className="ghost" onClick={openAddOverrideModal}>
                        {dict.team.budgets.addOverrideButton}
                      </button>
                    </div>
                    {budgetRemoveError && <div className="err">{budgetRemoveError}</div>}
                    <div className="table-scroll">
                      <table role="table" className="team-table">
                        <thead role="rowgroup">
                          <tr role="row">
                            <th role="columnheader">{dict.team.budgets.colMember}</th>
                            <th role="columnheader">{dict.team.budgets.colPeriod}</th>
                            <th role="columnheader">{dict.team.budgets.colLimit}</th>
                            <th role="columnheader" aria-label={dict.team.budgets.actionsAria} />
                          </tr>
                        </thead>
                        <tbody role="rowgroup">
                          {budgets.filter((b) => b.user_id).length === 0 && (
                            <tr role="row">
                              <td role="cell" colSpan={4}>
                                <p className="note">{dict.team.budgets.overridesEmpty}</p>
                              </td>
                            </tr>
                          )}
                          {budgets
                            .filter((b) => b.user_id)
                            .map((row) => (
                              <tr role="row" key={row.budget_id}>
                                <td role="cell" data-label={dict.team.budgets.colMember}>
                                  <span className="team-member-name">{row.display_name || row.email}</span>
                                </td>
                                <td role="cell" data-label={dict.team.budgets.colPeriod}>
                                  {dict.team.budgets.periodName[row.period_type]}
                                </td>
                                <td role="cell" data-label={dict.team.budgets.colLimit}>
                                  {formatTHB(row.limit.amount)}
                                </td>
                                <td role="cell" className="num" data-label={dict.team.budgets.actionsAria}>
                                  <div className="row-actions">
                                    <button type="button" className="ghost" onClick={() => openOverrideEditModal(row)}>
                                      {dict.team.budgets.editButton}
                                    </button>
                                    <button
                                      type="button"
                                      className="ghost danger-text"
                                      onClick={() =>
                                        setBudgetRemoveTarget({
                                          kind: 'member',
                                          userId: row.user_id,
                                          periodType: row.period_type,
                                        })
                                      }
                                    >
                                      {dict.team.budgets.removeButton}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </motion.section>
          )}
        </>
      )}

      <AnimatePresence>
        {removeTarget && (
          <ConfirmDialog
            title={dict.team.removeTitle}
            body={<p>{dict.team.removeBody(removeTarget.display_name || removeTarget.email, isB2BOrg)}</p>}
            confirmLabel={dict.team.removeConfirmLabel}
            danger
            busy={removeBusy}
            error={removeError}
            onConfirm={confirmRemove}
            onCancel={() => {
              setRemoveTarget(null);
              setRemoveError(null);
            }}
          />
        )}
        {revokeTarget && (
          <ConfirmDialog
            title={dict.team.cancelInviteTitle}
            body={<p>{dict.team.cancelInviteBody(revokeTarget.email)}</p>}
            confirmLabel={dict.team.cancelInviteConfirmLabel}
            danger
            busy={revokeBusy}
            error={revokeError}
            onConfirm={confirmRevoke}
            onCancel={() => {
              setRevokeTarget(null);
              setRevokeError(null);
            }}
          />
        )}
        {editTarget && (
          <Modal
            title={dict.team.editInviteTitle(editTarget.email)}
            onClose={() => {
              setEditTarget(null);
              setEditError(null);
            }}
          >
            <form onSubmit={confirmEditInvite} className="modal-body">
              {isOwner && (
                <>
                  <label htmlFor="edit-invite-role">{dict.team.roleLabel}</label>
                  <SelectMenu
                    id="edit-invite-role"
                    aria-label={dict.team.roleLabel}
                    value={editRole}
                    onChange={setEditRole}
                    options={[
                      { value: 'member', label: dict.team.role.member },
                      { value: 'admin', label: dict.team.role.admin },
                    ]}
                  />
                </>
              )}
              <label htmlFor="edit-invite-expires">{dict.team.expiresInLabel}</label>
              <SelectMenu
                id="edit-invite-expires"
                aria-label={dict.team.expiresInLabel}
                value={editExpiresInDays}
                onChange={setEditExpiresInDays}
                options={[
                  { value: '', label: dict.team.editKeepExpiry },
                  { value: '1', label: dict.team.expiresInOptions['1'] },
                  { value: '3', label: dict.team.expiresInOptions['3'] },
                  { value: '7', label: dict.team.expiresInOptions['7'] },
                  { value: '30', label: dict.team.expiresInOptions['30'] },
                  { value: '365', label: dict.team.expiresInOptions['365'] },
                ]}
              />
              {editError && <div className="err">{editError}</div>}
              <div className="modal-actions">
                <button
                  type="button"
                  className="ghost"
                  disabled={editBusy}
                  onClick={() => {
                    setEditTarget(null);
                    setEditError(null);
                  }}
                >
                  {dict.common.cancel}
                </button>
                <button type="submit" disabled={editBusy || (!isOwner && editExpiresInDays === '')}>
                  {editBusy ? dict.common.working : dict.team.editInviteSave}
                </button>
              </div>
            </form>
          </Modal>
        )}
        {budgetRemoveTarget && (
          <ConfirmDialog
            title={
              budgetRemoveTarget.kind === 'default' ? dict.team.budgets.removeDefaultTitle : dict.team.budgets.removeOverrideTitle
            }
            body={
              <p>
                {budgetRemoveTarget.kind === 'default'
                  ? dict.team.budgets.removeDefaultBody(dict.team.budgets.periodName[budgetRemoveTarget.periodType])
                  : dict.team.budgets.removeOverrideBody(
                      memberName(budgetRemoveTarget.userId),
                      dict.team.budgets.periodName[budgetRemoveTarget.periodType],
                    )}
              </p>
            }
            confirmLabel={dict.team.budgets.removeConfirmLabel}
            danger
            busy={budgetRemoveBusy}
            error={budgetRemoveError}
            onConfirm={confirmRemoveBudget}
            onCancel={() => {
              setBudgetRemoveTarget(null);
              setBudgetRemoveError(null);
            }}
          />
        )}
        {budgetModal && (
          <Modal
            title={
              budgetModal.kind === 'default'
                ? dict.team.budgets.modalSetDefaultTitle(dict.team.budgets.periodName[budgetModal.periodType])
                : budgetModal.locked
                  ? dict.team.budgets.modalEditOverrideTitle(memberName(budgetModal.userId))
                  : dict.team.budgets.modalAddOverrideTitle
            }
            onClose={() => {
              setBudgetModal(null);
              setBudgetSaveError(null);
            }}
          >
            <form onSubmit={submitBudgetModal} className="modal-body">
              {budgetModal.kind === 'member' &&
                (budgetModal.locked ? (
                  <p className="note">{memberName(budgetModal.userId)}</p>
                ) : (
                  <>
                    <label htmlFor="budget-member">{dict.team.budgets.memberLabel}</label>
                    <SelectMenu
                      id="budget-member"
                      aria-label={dict.team.budgets.memberLabel}
                      value={budgetModal.userId ?? ''}
                      onChange={(v) => setBudgetModal({ ...budgetModal, userId: v || undefined })}
                      options={[
                        { value: '', label: dict.team.budgets.memberPlaceholder },
                        ...(members ?? []).map((m) => ({ value: m.user_id, label: m.display_name || m.email })),
                      ]}
                    />
                  </>
                ))}
              <label htmlFor="budget-period">{dict.team.budgets.periodSelectLabel}</label>
              <SelectMenu
                id="budget-period"
                aria-label={dict.team.budgets.periodSelectLabel}
                value={budgetModal.periodType}
                disabled={budgetModal.locked}
                onChange={(v: BudgetPeriod) => setBudgetModal({ ...budgetModal, periodType: v })}
                options={[
                  { value: 'daily', label: dict.team.budgets.periodName.daily },
                  { value: 'monthly', label: dict.team.budgets.periodName.monthly },
                ]}
              />
              <label htmlFor="budget-amount">{dict.team.budgets.amountLabel}</label>
              <input
                id="budget-amount"
                type="text"
                inputMode="decimal"
                value={budgetModal.amount}
                onChange={(e) => setBudgetModal({ ...budgetModal, amount: e.target.value })}
                placeholder={dict.team.budgets.amountPlaceholder}
                required
              />
              {budgetSaveError && <div className="err">{budgetSaveError}</div>}
              <div className="modal-actions">
                <button
                  type="button"
                  className="ghost"
                  disabled={budgetSaveBusy}
                  onClick={() => {
                    setBudgetModal(null);
                    setBudgetSaveError(null);
                  }}
                >
                  {dict.common.cancel}
                </button>
                <button
                  type="submit"
                  disabled={budgetSaveBusy || budgetModal.amount.trim() === '' || (budgetModal.kind === 'member' && !budgetModal.userId)}
                >
                  {budgetSaveBusy ? dict.team.budgets.saving : dict.team.budgets.save}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}
